import { DateTime } from "luxon";
import http from "@/BaseServiceHttp";

export const SHIFT_REPORT_TO_CREATE = "CREATE";
export const SHIFT_REPORT_GRACE_PERIOD_EXPIRED = "EXPIRED";
export const SHIFT_REPORT_TO_UPDATE = "UPDATE";
const ONE_HOUR_MS = 60 * 60 * 1000;

class ShiftReportService {
  update(id, productionUnitId, payload) {
    return http.put(`production-units/${productionUnitId}/end-of-shift-reports/${id}`, payload);
  }

  create(productionUnitId, payload) {
    return http.post(`production-units/${productionUnitId}/end-of-shift-reports`, payload);
  }

  get(productionUnitId, startTime) {
    return http.get(`production-units/${productionUnitId}/end-of-shift-reports?startTime=${startTime}`);
  }

  buildWorkShiftReport({ rawWorkShiftReports, productionUnit, workShiftCoverage, users, sortedWorkShiftPuMetrics }) {
    const sortedWorkShiftReports = rawWorkShiftReports.sort(
      (a, b) => b.work_shift_start_time - a.work_shift_start_time,
    );
    const transformedWorkShiftReports = this.getTransformedWorkShiftReports(
      workShiftCoverage,
      sortedWorkShiftReports,
      sortedWorkShiftPuMetrics,
      users,
      productionUnit,
    );

    const reports = this.fillPreviousMetricCount(transformedWorkShiftReports);
    const firstReport = reports[0];
    let previousMetric;
    if (firstReport) {
      previousMetric = sortedWorkShiftPuMetrics.filter(
        (it) => it.key.work_shift_id !== firstReport.workShiftId && it.key.start_time < firstReport.workShiftStartTime,
      );
    }
    if (previousMetric) {
      firstReport.previousShiftCount = this.getCountFromBaseUnit(previousMetric, productionUnit.converted_unit_name);
    }
    return reports;
  }

  getTransformedWorkShiftReports(
    workShiftCoverage,
    sortedWorkShiftReports,
    sortedWorkShiftPuMetrics,
    users,
    productionUnit,
  ) {
    return workShiftCoverage
      .filter((it) => it.work_shift_id !== null)
      .sort((a, b) => a.start_millis_utc - b.start_millis_utc)
      .map((coverage) => {
        const report = sortedWorkShiftReports.find((wsr) => {
          return (
            wsr.work_shift_id === coverage.work_shift_id && wsr.work_shift_start_time === coverage.start_millis_utc
          );
        });
        const currentMetrics = this.getCurrentMetrics({ sortedWorkShiftPuMetrics, coverage });
        if (report) {
          return this.buildExistingShiftReport(coverage, report, users, currentMetrics, productionUnit);
        } else {
          return this.createReport({
            coverage,
            productionUnit: productionUnit,
            currentMetrics: currentMetrics,
          });
        }
      });
  }

  fillPreviousMetricCount(transformedWorkShiftReports) {
    return transformedWorkShiftReports.map((report, index, arr) => {
      // Get the previous element if it exists; otherwise, it will be null
      const previousReport = index > 0 ? arr[index - 1] : null;
      return {
        ...report,
        previousShiftCount: previousReport?.currentShiftCount,
      };
    });
  }

  buildExistingShiftReport(coverage, report, users, currentMetrics, productionUnit) {
    const productionUnitBaseUnit = productionUnit.converted_unit_name;
    return {
      workShiftId: coverage.work_shift_id,
      workShiftStartTime: coverage.start_millis_utc,
      workShiftEndTime: coverage.end_millis_utc,
      workShiftName: coverage.work_shift_name,
      shiftTargetQty: report.shift_target_qty,
      shiftTargetUnit: report.shift_target_unit,
      userDisplayName: this.findUser(users, report.last_modified_by),
      sentiment: report.sentiment,
      comments: report.comments,
      creationTime: report.creation_time,
      associatedWorkShiftCoverage: coverage,
      type: SHIFT_REPORT_TO_UPDATE,
      currentShiftCount: this.getCountFromBaseUnit(currentMetrics?.metrics, productionUnitBaseUnit),
      productionUnitBaseUnit: productionUnitBaseUnit,
      id: report.id,
    };
  }

  findUser(users, modifiedById) {
    return users?.find((user) => user.id === modifiedById)?.display_name;
  }

  getCurrentMetrics({ sortedWorkShiftPuMetrics, coverage }) {
    return sortedWorkShiftPuMetrics.find((it) => {
      return it.key.start_time === coverage.start_millis_utc;
    });
  }

  getTimeLeftInMs(coverage) {
    const timeLeft = coverage.end_millis_utc + ONE_HOUR_MS - DateTime.utc().toMillis();
    return Math.max(timeLeft, 0);
  }

  isMarkedForCreation(workShiftId, endTimeMs) {
    return workShiftId !== null && endTimeMs + ONE_HOUR_MS > DateTime.utc().toMillis();
  }

  createReport({ coverage, currentMetrics, productionUnit }) {
    const isCreatable = this.isMarkedForCreation(
      coverage.work_shift_id,
      coverage.end_millis_utc ?? DateTime.utc().toMillis(),
    );
    const productionUnitBaseUnit = productionUnit.converted_unit_name;
    return isCreatable
      ? {
          workShiftId: coverage.work_shift_id,
          workShiftName: coverage.work_shift_name,
          workShiftStartTime: coverage.start_millis_utc,
          workShiftEndTime: coverage.end_millis_utc,
          associatedWorkShiftCoverage: coverage,
          type: SHIFT_REPORT_TO_CREATE,
          timeLeftMs: this.getTimeLeftInMs(coverage),
          reportEndTime: coverage.end_time_millis + ONE_HOUR_MS,
          currentMetrics: currentMetrics,
          currentShiftCount: this.getCountFromBaseUnit(currentMetrics?.metrics, productionUnitBaseUnit),
          productionUnitBaseUnit: productionUnitBaseUnit,
        }
      : {
          workShiftId: coverage.work_shift_id,
          workShiftName: coverage.work_shift_name,
          workShiftStartTime: coverage.start_millis_utc,
          workShiftEndTime: coverage.end_millis_utc,
          associatedWorkShiftCoverage: coverage,
          type: SHIFT_REPORT_GRACE_PERIOD_EXPIRED,
          currentMetrics: currentMetrics,
          currentShiftCount: this.getCountFromBaseUnit(currentMetrics?.metrics, productionUnitBaseUnit),
          productionUnitBaseUnit: productionUnitBaseUnit,
        };
  }

  getCountFromBaseUnit(metrics, productionUnitBaseUnit) {
    return metrics?.produced_quantity?.total_count_by_unit?.[productionUnitBaseUnit];
  }
}

export default new ShiftReportService();
